import React, { useState } from "react";
import {
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "./Iconify";
import { isEmpty } from "lodash";
import Api from "../lib/api";
import { updatePbxInfo } from "../redux/slices/auth";
import ConfirmDialog from "./ConfirmDialog";

// Define a functional component called `MakeCallButton`
export default function MakeCallTrigger({
  phone,
  disabled = false,
  contactId,
  contactName,
}) {
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  // Destructure props to extract phone, disabled, contactId, and contactName

  // Local state for managing the anchor element for the dropdown menu
  const [anchorEl, setAnchorEl] = useState(null);

  // Access authUser and pbxConfig from the Redux store using useSelector
  const { authUser, pbxConfig } = useSelector((state) => state.auth);

  // Get dispatch method from useDispatch to dispatch actions to the Redux store
  const dispatch = useDispatch();

  // Handle opening of the menu by setting the anchor element
  const handleClickMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle closing of the menu by clearing the anchor element
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Function to prepare the PBX configuration for making a call
  const makeACallMenu = (phone, name = "Unknown", id = null) => {
    let tmp = {};

    // Update PBX configuration, including call details (phone, name, id)
    tmp = {
      ...pbxConfig,
      makeACall: true,
    };

    // Set the phone number, name, and ID for the call
    tmp.callToPhone = phone;
    tmp.callToName = name;
    tmp.callToId = id ? id : null;

    // Dispatch an action to update PBX info in the Redux store
    dispatch(updatePbxInfo(tmp));
  };

  // Function to format phone numbers to a standardized format
  const formatPhoneNumber = (phone, prefix) => {
    // If the phone number has 11 digits, remove the first digit (assumed to be the country code)
    var formattedPhone = phone.length == 11 ? phone.slice(1, 11) : phone;

    // Return the formatted phone number with the prefix
    return prefix + formattedPhone;
  };

  // Function to handle making a call, validating the phone number, and showing confirmation dialogs
  const runClickToCall2 = async (prefix = "+1") => {
    // Validate the phone number using the API
    const { data } = await Api.validatePhoneNumberForVoice(phone);

    // Open a confirmation dialog to confirm the call
    setConfirmDialog({
      isOpen: true,
      title: data
        ? "The phone " + phone + " was marked as not valid for calls " // Message if the phone number is not valid
        : `Are you sure you want to call `,
      subTitle: data
        ? "Are you sure you want to call it anyway?" // Subtitle for invalid number confirmation
        : contactName
        ? `${contactName} ( ${phone} )?` // Show contact name if available
        : phone + "?", // Show only the phone number otherwise
      onConfirm: () => {
        handleCloseMenu();
        setConfirmDialog({
          isOpen: false,
          title: "",
          subTitle: "",
        });
        // If web phone is used, format the number and trigger the call via PBX
        makeACallMenu(formatPhoneNumber(phone, prefix), contactName, contactId);
      },
    });
  };

  // Function to prepare phone options (e.g., user's own number, main office number, etc.)
  const phoneOptions = () => {
    var options = [];

    // Add the user's own phone number to the options if available
    if (authUser?.phone || false) {
      options.push({
        label: (
          <Typography>
            <strong style={{ fontWeight: 500 }}> My number: </strong>
            {authUser.phone}
          </Typography>
        ),
        prefix: "+1",
      });
    }

    // Add the main building's phone number if available
    if (authUser?.building?.data?.phone || false) {
      options.push({
        label: (
          <Typography>
            <strong style={{ fontWeight: 500 }}> Main number: </strong>
            {authUser.building.data.phone}
          </Typography>
        ),
        prefix: "*444",
      });
    }

    // If the user belongs to communication groups, add those phone numbers
    if (!isEmpty(authUser.communications_groups)) {
      authUser.communications_groups
        .filter((group) => {
          return group.type !== "message_group"; // Only include groups not related to messaging
        })
        .forEach((group) => {
          if (
            group.outbound_call_code &&
            group.phone &&
            group.type !== "message_group"
          ) {
            // Add the group's phone number and call code to the options
            options.push({
              label: (
                <Typography>
                  <strong style={{ fontWeight: 500 }}>
                    {" "}
                    {`${group.name}: `}:{" "}
                  </strong>
                  {group.phone}
                </Typography>
              ),
              prefix: group.outbound_call_code,
            });
          }
        });
    }
    return options;
  };

  // If no phone options are available, return nothing
  if (phoneOptions().length == 0) {
    return <></>;
  }

  // Return JSX with the call button and dropdown menu
  return (
    <>
      {phone &&
        authUser.organization &&
        authUser.organization.data &&
        authUser.organization.data.pbx_integration === 1 && // Only show the call button if PBX integration is enabled
        pbxConfig &&
        pbxConfig.aor && (
          <Tooltip
            title={
              "Call " + (contactName ? contactName + ` (${phone})` : phone)
            }
          >
            <span>
              <IconButton
                aria-label="make a call"
                color="primary"
                disabled={disabled} // Disable the button if the disabled prop is true
                onClick={(e) => {
                  handleClickMenu(e); // Open the menu on button click
                }}
              >
                <Iconify
                  icon={"solar:outgoing-call-bold"} // Icon for the call button
                  width={25}
                  height={25}
                  style={{
                    color: disabled ? "#0d47a1" : "#2196f3", // Color changes based on disabled state
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}

      {/* Dropdown menu for selecting the phone number to call from */}
      <Menu
        id="simple-menu"
        anchorEl={anchorEl} // Set the menu position relative to the anchor element
        open={Boolean(anchorEl)} // Open the menu if the anchor element is set
        onClose={handleCloseMenu} // Close the menu when the user clicks outside
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography
          style={{
            margin: "0px 10px",
            padding: "2px 6px",
            fontWeight: 600,
          }}
        >
          Call from:{" "}
        </Typography>

        {/* Display phone options in the menu */}
        <div
          style={{
            backgroundColor: "#efefef9e",
            margin: "0px 5px",
            borderRadius: 10,
          }}
        >
          {phoneOptions().map((option) => {
            return (
              <MenuItem onClick={() => runClickToCall2(option.prefix)}>
                {option.label}
              </MenuItem>
            );
          })}
        </div>
      </Menu>
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </>
  );
}
